import Button from '../components/Button'
import CtaBand from '../components/CtaBand'
import Icon from '../components/Icon'
import PageHero from '../components/PageHero'
import Reveal from '../components/Reveal'
import { Section, SectionHead } from '../components/Section'
import { app } from '../data/site'
import { useSeo } from '../lib/seo'

/** What the app is for. Both paragraphs are verbatim from the old site. */
const INTRO = [
  'Der Aufzug ist defekt, das Licht im Treppenhaus brennt nicht mehr oder Sie haben eine Frage zur Abrechnung? Dann haben Sie als Mieter oder Eigentümer die Möglichkeit, Ihre Anliegen ganz einfach per Smartphone über die CASA App zu melden - inklusive Fotoanhang. Wir halten Sie über den Bearbeitungsstatus Ihrer Anfrage auf dem Laufenden.',
  'Zusätzlich haben Sie jederzeit wichtige Dokumente zu Ihrer Immobilie griffbereit. Wenn Sie unsere Kunden-App am Handy installiert haben, erhalten Sie ebenso eine Push-Benachrichtigung über Neuigkeiten am digitalen Schwarzen Brett. Dadurch bleiben Sie mit Sicherheit immer auf dem neuesten Stand zu Themen rund um Ihre Immobilie.',
]

const HIGHLIGHTS = [
  { icon: 'wrench', label: 'Probleme lösen' },
  { icon: 'folder', label: 'Gebündelte Informationen' },
  { icon: 'shield', label: 'Einzigartige Sicherheit' },
]

/** How to get access. */
const STEPS = [
  {
    title: 'Einladung erhalten',
    text: 'Sie erhalten von uns eine E-Mail mit einer persönlichen Einladung zum Beitritt der iDWELL App.',
  },
  {
    title: 'Anmeldung bestätigen',
    text: 'Drücken Sie auf den Button „Anmeldung bestätigen“ und geben Sie Ihr persönlich gewähltes Passwort ein.',
  },
  {
    title: 'App installieren',
    text: 'Laden Sie sich die iDWELL App für Android oder iOS herunter - oder nutzen Sie den Zugang direkt im Browser.',
  },
  {
    title: 'Loslegen',
    text: 'Und schon können Sie alle Vorteile unserer digitalen Dienstleistung nutzen.',
  },
]

/** What the app does for the reader. Verbatim from the old site. */
const BENEFITS = [
  {
    icon: 'folder',
    title: 'Transparenz ist uns ein Anliegen',
    text: 'Auf der App finden Sie alle wichtigen Dokumente zu Ihrer Liegenschaft. Profitieren Sie von mehr Übersicht und ständiger Verfügbarkeit.',
  },
  {
    icon: 'bolt',
    title: 'Effizienz gehört zu unserem Tagesgeschäft',
    text: 'Ihre Schadensmeldungen und sonstige Anliegen werden rasch bearbeitet und Sie erhalten regelmäßig per Push-Nachricht ein Status-Update.',
  },
  {
    icon: 'clipboard',
    title: 'Dokumentation ist die „halbe Miete“',
    text: 'Mit der App erhalten Sie alle wichtigen Informationen zu Ihrer Liegenschaft direkt am schwarzen Brett und können diese jederzeit abrufen.',
  },
  {
    icon: 'route',
    title: 'Mobilität und Zeitersparnis',
    text: 'Informieren Sie sich zu jeder Zeit und an jedem Ort rund um Ihre Immobilie auf der CASA App.',
  },
]

/**
 * A drawn approximation of the portal screen - the real app is iDWELL's, and the
 * original banner image is among the missing uploads. It shows the three things
 * the page promises: a report with a photo, documents, and the noticeboard. It
 * does not imitate iDWELL's branding.
 */
function PhoneMock() {
  return (
    <div
      className="phone"
      role="img"
      aria-label="Darstellung der CASA App auf einem Smartphone"
    >
      <div className="phone__head">
        <span className="phone__title">CASA App</span>
        <span className="phone__dot" aria-hidden="true" />
      </div>

      <div className="phone__card phone__card--accent">
        <span className="phone__label">Meldung · in Bearbeitung</span>
        <p className="phone__text">Aufzug Haus B steht still</p>
        <div className="phone__bar" style={{ marginTop: '0.55rem' }}>
          <div className="phone__bar phone__bar--fill" />
        </div>
      </div>

      <div className="phone__card">
        <span className="phone__label">Schwarzes Brett</span>
        <p className="phone__text">
          Treppenhausreinigung: neuer Turnus ab Montag
        </p>
      </div>

      <div className="phone__card">
        <span className="phone__label">Dokumente</span>
        {['Betriebskostenabrechnung', 'Hausordnung'].map((document) => (
          <div className="phone__row" style={{ marginTop: '0.4rem' }} key={document}>
            <Icon name="doc" />
            <span className="phone__text" style={{ margin: 0 }}>
              {document}
            </span>
          </div>
        ))}
      </div>

      <div className="phone__card">
        <span className="phone__label">Neue Meldung</span>
        <div className="phone__row" style={{ marginTop: '0.45rem' }}>
          <Icon name="camera" className="is-gold" />
          <span className="phone__text" style={{ margin: 0 }}>
            Foto anhängen
          </span>
        </div>
      </div>

      <div className="phone__spacer" />

      <div className="phone__tabs" aria-hidden="true">
        <Icon name="home" className="is-active" />
        <Icon name="clipboard" />
        <Icon name="folder" />
        <Icon name="bell" />
        <Icon name="user" />
      </div>
    </div>
  )
}

export default function CasaApp() {
  useSeo({
    title: 'CASA App',
    description:
      'Mit der CASA App melden Mieter und Eigentümer Anliegen per Smartphone - inklusive Fotoanhang, Dokumentenablage und digitalem schwarzem Brett. In Zusammenarbeit mit iDWELL.',
    path: '/casa-app',
  })

  return (
    <>
      <PageHero
        eyebrow="CASA App"
        title="Unkompliziert. Persönlich. Nah."
        lead="Anliegen per Smartphone melden, Dokumente jederzeit griffbereit, Neuigkeiten direkt aufs Handy - für Mieter und Eigentümer."
        breadcrumb={[{ label: 'CASA App' }]}
      >
        <div className="btn-row">
          <Button href={app.loginUrl} icon="arrowUpRight">
            Zum Login
          </Button>
          <Button href={app.portalUrl} variant="ghost-dark" icon="arrowUpRight">
            {app.portalUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
          </Button>
        </div>
      </PageHero>

      <Section>
        <div className="split">
          <Reveal>
            <p className="eyebrow">Wofür Sie die App nutzen</p>
            <h2 style={{ marginTop: 'var(--s-4)' }}>
              Ihr Anliegen, in zwei Minuten gemeldet
            </h2>
            {INTRO.map((paragraph, index) => (
              <p
                key={paragraph.slice(0, 32)}
                style={{
                  marginTop: 'var(--s-5)',
                  ...(index === 0
                    ? {
                        fontSize: 'var(--step-1)',
                        lineHeight: 1.6,
                        color: 'var(--text)',
                      }
                    : null),
                }}
              >
                {paragraph}
              </p>
            ))}

            <ul
              className="check-list"
              style={{ marginTop: 'var(--s-6)', gap: 'var(--s-4)' }}
            >
              {HIGHLIGHTS.map((highlight) => (
                <li key={highlight.label}>
                  <Icon name={highlight.icon} />
                  <span style={{ fontWeight: 600, color: 'var(--text)' }}>
                    {highlight.label}
                  </span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal>
            <PhoneMock />
          </Reveal>
        </div>
      </Section>

      <Section tone="paper">
        <SectionHead
          eyebrow="Zugang erhalten"
          title="Wie Sie Zugriff zur iDWELL App erhalten"
          lead="Sie haben von uns keine Einladung via SMS oder E-Mail erhalten? Dann melden Sie sich bitte bei uns, damit wir Sie freischalten."
        />

        <div className="split" style={{ alignItems: 'start' }}>
          <Reveal>
            <ol className="numbered">
              {STEPS.map((step) => (
                <li key={step.title}>
                  <p>
                    <strong>{step.title}</strong>
                    {step.text}
                  </p>
                </li>
              ))}
            </ol>
          </Reveal>

          <Reveal className="card" style={{ display: 'grid', gap: 'var(--s-5)' }}>
            <div>
              <p className="eyebrow">Download</p>
              <h3 style={{ marginTop: 'var(--s-3)', fontSize: 'var(--step-1)' }}>
                App für Ihr Mobiltelefon
              </h3>
            </div>
            <div className="btn-row">
              <Button href={app.android} variant="outline" icon="arrowUpRight">
                Android
              </Button>
              <Button href={app.ios} variant="outline" icon="arrowUpRight">
                iOS
              </Button>
            </div>
            <p style={{ margin: 0, fontSize: 'var(--step--1)' }}>
              Oder direkt im Browser über{' '}
              <a href={app.portalUrl} target="_blank" rel="noopener noreferrer">
                {app.portalUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
              </a>
              .
            </p>
            <div
              style={{
                paddingTop: 'var(--s-5)',
                borderTop: '1px solid var(--border)',
              }}
            >
              <Button href={app.loginUrl} icon="arrowRight" block>
                Hier finden Sie den Login
              </Button>
            </div>
          </Reveal>
        </div>
      </Section>

      <Section>
        <SectionHead
          eyebrow="Ihre Vorteile"
          title="Folgende Vorteile bietet Ihnen unser App-Service konkret"
        />
        <Reveal group className="grid grid--2">
          {BENEFITS.map((benefit) => (
            <article className="card card--lift feature" key={benefit.title}>
              <span className="icon-tile">
                <Icon name={benefit.icon} />
              </span>
              <h3 className="feature__title">{benefit.title}</h3>
              <p>{benefit.text}</p>
            </article>
          ))}
        </Reveal>

        <p
          style={{
            marginTop: 'var(--s-7)',
            fontSize: 'var(--step--1)',
            color: 'var(--text-meta)',
          }}
        >
          In Zusammenarbeit mit{' '}
          <a href={app.partnerUrl} target="_blank" rel="noopener noreferrer">
            {app.partner}
          </a>
          .
        </p>
      </Section>

      <CtaBand />
    </>
  )
}
